import { api } from "@ts/core/api";
import { Cookie } from "@ts/core/Cookie";
import { Elements } from "@ts/core/Elements";
import { popError } from "@ts/core/Popup";
import { yesNo } from "@ts/core/YesNo";
import { simpleForm } from "@ts/core/SimpleForm";
import { login } from "@ts/login";
import { logout } from "@ts/logout";
import { Page } from "./Page";
import { Utils } from "./core/Utils";

export enum MenuType {
    Page = 'page',
    Login = 'login',
    Logout = 'logout',
    Theme = 'theme',
    AddGroup = 'addGroup',
    AddPage = 'addPage'
}

export interface MenuData {
    pagename: string;
    pagetitle: string;
    childof: string;
    childs: Array<MenuData>;
}

export enum CollapseSymbol {
    Collapse = '⯇',
    Expand = '⯈'
}

export interface UserPriv {
    admin: boolean;
    edit: boolean;
}

export class MainMenu {

    async load(): Promise<void> {

        let userPriv: UserPriv = { admin: false, edit: false };
        const username = Cookie.username;
        if (username != '') {
            const user = await api<UserPriv>('getUserPriv', {
                pagename: Cookie.pagename,
                username: username
            });
            if (user) {
                userPriv = user;
            }
        }

        const toggler = document.body.querySelector('#menutoggler') as HTMLDivElement;

        // prevent adding listener twice (after login)
        const listens = toggler.getAttribute('data-listens');
        if (listens === null || listens === 'false') {
            toggler.setAttribute('data-listens', 'true');
            toggler.addEventListener('click', (e: Event) => {
                e.preventDefault();
                this.toggleMenu();
            });
        }

        const resp = await api<string>('getMenu');
        if (!resp) {
            popError(`Kunde inte ladda menyn`);
            return;
        }

        const menu = JSON.parse(resp);
        Elements.menu.innerHTML = '';
        this.renderMenu(userPriv, menu);

    }

    private renderMenu(userPriv: UserPriv, menu: Array<MenuData>): void {

        Elements.help.classList.add('hidden');
        for (let i = 0; i < menu.length; i++) {
            this.renderMenuItem(userPriv, Elements.menu, menu[i]);
        }
    }

    private renderMenuItem(userPriv: UserPriv, parent: HTMLElement, menu: MenuData): void {

        if (parent && menu.childs.length > 0) {
            this.renderGroup(userPriv, parent, menu);
            return;
        }
        const button = document.createElement('button');

        let cls = 'page';
        if (menu.pagename === MenuType.AddGroup) cls = MenuType.AddGroup;
        else if (menu.pagename === MenuType.AddPage) cls = MenuType.AddPage;
        button.classList.add(cls);

        button.innerText = menu.pagetitle;
        button.id = menu.pagename;
        button.addEventListener('click', (e: MouseEvent) => {
            e.preventDefault();
            this.onMenuSelect(e);
        });
        parent.appendChild(button);
    }

    private renderGroup(userPriv: UserPriv, parentElement: HTMLElement, parentItem: MenuData): void {

        const groupElement = document.createElement('button');
        groupElement.classList.add('submenu');
        groupElement.innerText = `${parentItem.pagetitle} ${CollapseSymbol.Expand}`;
        parentElement.appendChild(groupElement);
        groupElement.addEventListener('mousedown', (e: MouseEvent) => { this.onMenuSelect(e); });

        const container = document.createElement('div');
        container.id = parentItem.pagename;
        container.classList.add('hidden');
        container.classList.add('sub');
        parentElement.appendChild(container);

        this.renderGroupItems(userPriv, container, parentItem.childs);
    }

    private renderGroupItems(userPriv: UserPriv, parentElement: HTMLElement, items: Array<MenuData>): void {

        items.forEach(item => {

            if (item.childs.length > 0) {
                this.renderGroup(userPriv, parentElement, item);
            }
            else {
                const button = document.createElement('button');

                let cls = 'page';
                if (item.pagename === MenuType.AddGroup) cls = MenuType.AddGroup;
                else if (item.pagename === MenuType.AddPage) cls = MenuType.AddPage;
                button.classList.add(cls);

                button.innerText = item.pagetitle;
                button.id = item.pagename;
                parentElement.appendChild(button);

                button.addEventListener('mousedown', async (e: MouseEvent) => {
                    e.preventDefault();
                    this.onMenuSelect(e);
                });
            }
        });
    }

    private toggleTheme(): string {
        const current = Cookie.theme;
        const next = current === "theme-dark" ? "theme-light" : "theme-dark";
        document.documentElement.setAttribute("data-theme", next);
        Cookie.theme = next;
        return next;
    }

    private toggleMenu(): void {
        const menu = Elements.menu;
        if (menu.classList.contains('hidden')) menu.classList.remove('hidden');
        else menu.classList.add('hidden');
    }

    private async onMenuSelect(e: MouseEvent): Promise<void> {

        const button = e.target as HTMLButtonElement;
        if (button === null) return;

        if (e.ctrlKey) {

            const info = {
                isGroup: button.classList.contains('submenu'),
                hasParent: button.parentElement?.tagName.toLowerCase() === 'div',
                parentTitle: button.parentElement?.innerText,
                parentName: button.parentElement?.id,
                pageName: button.id,
                pageTitle: button.innerText
            }
            console.log(info);

            const form = document.createElement('form');
            form.className = 'pageedit';
            form.innerHTML = Utils.minify( `
                <h2>Vad vill du göra</h2>
                <button id="addgroup">Lägga till en gruppsida</button>
                <button id="addpage">Lägga till en sida</button>
                <button id="renamepage">Byt titel</button>
                <button id="deletepage">Radera sidan</button>
                <button id="nothing">Ingenting</button>
                `);
            document.body.appendChild(form);
            Utils.overlay(true);

            const btnAddGroup = form.querySelector('#addgroup') as HTMLButtonElement;
            const btnAddPage = form.querySelector('#addpage') as HTMLButtonElement;
            const btnRenamePage = form.querySelector('#renamepage') as HTMLButtonElement;
            const btnDeletePage = form.querySelector('#deletepage') as HTMLButtonElement;
            const btnNothing = form.querySelector('#nothing') as HTMLButtonElement;

            btnAddGroup.addEventListener('click', async (e: Event) => {
                e.preventDefault();
                simpleForm('Grupp', 1, 'Gruppens titel', '', 'Titel')
                .then( (groupName:string) => {
                    if (groupName != '') {
                    }
                    form.remove();
                    Utils.overlay(false);
                });
            });

            btnAddPage.addEventListener('click', async (e: Event) => {
                e.preventDefault();
                await simpleForm('Sidans titel', 1, 'Ny titel', button.innerText, 'Titel')
                .then((pageName: string) => {
                    if (pageName != '' && button.innerText) {
                    }
                    form.remove();
                    Utils.overlay(false);
                });
            });

            btnRenamePage.addEventListener('click', async (e: Event) => {
                e.preventDefault();
                await simpleForm('Sidans titel', 1, 'Ny titel', button.innerText, 'Titel')
                .then((pageName: string) => {
                    if (pageName != '' && button.innerText) {
                    }
                    form.remove();
                    Utils.overlay(false);
                });
            });

            btnDeletePage.addEventListener('click', async (e: Event) => {
                e.preventDefault();
                if (await yesNo('Radera sida', `Är du säker på att du vill radera sidan "${button.innerText}"?`)) {
                }
                form.remove();
                Utils.overlay(false);
            });

            btnNothing.addEventListener('click', (e: Event) => {
                e.preventDefault();
                form.remove();
                Utils.overlay(false);
            });

        }

        if (button.id === MenuType.Login) {
            this.toggleMenu();
            login();
        }
        else if (button.id === MenuType.Logout) {
            this.toggleMenu();
            logout();
        }
        else if (button.id === MenuType.Theme) {
            const newTheme = this.toggleTheme();
            button.textContent = newTheme === "theme-dark" ? "Mörk 🌙" : "Ljus ☀️";
        }
        else {
            if (button.classList.contains(MenuType.Page)) {
                this.toggleMenu();
                const page = new Page;
                await page.load(button.id);
            }
            else {
                if (button.nextElementSibling?.classList.contains('hidden'))
                    button.nextElementSibling?.classList.remove('hidden');
                else
                    button.nextElementSibling?.classList.add('hidden');
            }
        }
    }
}
